import { BiEraser } from "react-icons/bi";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { IoCloseOutline } from "react-icons/io5";
import { IconButton, Tooltip } from "@mui/joy";
import { FaArrowRotateRight } from "react-icons/fa6";
import { ApiUpdateEntity, RefreshCache } from "@/legacy/ApiCallerOld";
import { CacheSlices } from "@/Store/Reducers/cache/CacheTypes";
import { useCache } from "@/Hooks/useCache";
import { useAgent } from "@/Hooks/useAgent";
import api from "@/Api/Api";
import MultiEntitySelect from "../FormComponents/MultiEntitySelect/MultiEntitySelect";
import { useAppDispatch, useAppSelector } from "../../Store/hooks";
import { customFilterApplied, customFilterCleared, customFilterOpened, customFilterPanelCleared, customViewCleared, customViewCreated, customViewUpdated, viewChanged } from "../../Store/Reducers/ticketsPageSlice";
import { Button } from "../Button/Button";
import { EmojiKeyboard } from "@shared/Components/FormComponents/EmojiKeyboard/EmojiKeyboard";
import { ViewVisibility } from "@shared/Enums/Enums";
import Dropdown, { DropdownOption } from "@shared/Components/FormComponents/Dropdown/Dropdown";
import { View, ViewSetFilter, ViewTextFilter, isViewNumberFilter, isViewSetFilter, isViewTextFilter } from "@shared/Models/View";
import TextInput from "@shared/Components/FormComponents/TextInput/TextInput";
import { Entities } from "@shared/Entities/Entities";

interface EntityTableFiltersProps {

}

/**
 * This currently just works for the tickets page. Could be generalised to work for EntityTable though.
 */
export function EntityTableFilters(props: EntityTableFiltersProps) {
	const dispatch = useAppDispatch();
	const cache = useCache();

	const viewId = useAppSelector(state => state.ticketsPage.viewId);
	const filters = useAppSelector(state => state.ticketsPage.customFilters);
	const customView = useAppSelector(state => state.ticketsPage.customView);
	const agent = useAgent();

	function getMultiNumberFilterValue(key: string): number[] | undefined {
		const filter = filters != null ? filters[key] : null;

		if (filter != null) {
			if (isViewNumberFilter(filter)) {
				return [filter.value];
			} else if (isViewSetFilter(filter)) {
				return filter.values;
			}
		}
	}

	function setMultiNumberFilterValue(key: string, newValue: number[] | undefined) {
		const newFilters = { ...filters };

		if (newValue != null) {
			const condition: ViewSetFilter = {
				type: "set",
				comparison: "contains",
				values: newValue
			};

			newFilters[key] = condition;
		} else {
			delete newFilters[key];
		}

		dispatch(customFilterApplied(newFilters));
	}

	function getStringFilterValue(key: string): string | undefined {
		const filter = filters != null ? filters[key] : null;

		if (filter != null && isViewTextFilter(filter)) {
			return filter.value;
		}
	}

	function setStringFilterValue(key: string, newValue: string) {
		const newFilters = { ...filters };

		if (newValue != null) {
			const condition: ViewTextFilter = {
				type: "text",
				comparison: "contains",
				value: newValue
			};

			newFilters[key] = condition;
		} else {
			delete newFilters[key];
		}

		dispatch(customFilterApplied(newFilters));
	}

	function resetFilters() {
		// Get current view's filters. Default to blank.
		const filters = cache.getView(viewId)?.filters ?? {};

		dispatch(customFilterOpened(filters));
	}

	function clearFilters() {
		dispatch(customFilterApplied({}));
	}

	function toggleFilters() {
		dispatch(customFilterPanelCleared());
	}

	function updateNewCustomViewName(newValue: string) {
		if (customView != null) {
			const newView: View = { ...customView };
			newView.name = newValue;

			dispatch(customViewUpdated(newView));
		}
	}

	function updateNewIcon(newValue: string) {
		if (customView != null) {
			const newView: View = { ...customView };
			newView.icon = newValue;

			dispatch(customViewUpdated(newView));
		}
	}

	function updateVisibility(_key: string, newValue: ViewVisibility) {
		if (customView != null) {
			const newView: View = { ...customView };
			newView.visibility = newValue;

			if (newValue != ViewVisibility.TEAMS) {
				newView.visibilityTeamsIds = [];
			}

			if (newValue != ViewVisibility.AGENTS) {
				newView.visibilityAgentsIds = [];
			}

			dispatch(customViewUpdated(newView));
		}
	}

	function updateAgentVisibility(_key: string, newIds?: number[]) {
		if (customView != null && newIds != null) {
			const newView: View = { ...customView };
			newView.visibilityAgentsIds = newIds;

			dispatch(customViewUpdated(newView));
		}
	}

	function updateTeamVisibility(_key: string, newIds?: number[]) {
		if (customView != null && newIds != null) {
			const newView: View = { ...customView };
			newView.visibilityTeamsIds = newIds;

			dispatch(customViewUpdated(newView));
		}
	}

	async function postView() {
		if (customView != null && filters != null) {
			if (customView.name == null || customView.name == "") {
				toast.error("View name must not be empty.");
				return;
			}

			const customViewCopy = { ...customView };

			customViewCopy.filters = filters;
			customViewCopy.creatorAgentId = agent?.id;

			const createRes = await api.createEntity<View>(Entities.VIEW, customViewCopy);

			if (createRes.successful && createRes.data != null) {
				dispatch(customFilterPanelCleared());
				dispatch(viewChanged(createRes.data));

				await RefreshCache(dispatch);
			} else {
				toast.error("Error. " + createRes.errorCode + ": " + createRes.errorMsg);
			}
		}
	}

	async function updateView() {
		if (customView != null && customView.id != null && filters != null) {
			const customViewCopy = { ...customView };

			customViewCopy.filters = filters;

			if (customViewCopy.id == undefined || typeof customViewCopy.id == "string") {
				return;
			}

			const createRes = await ApiUpdateEntity(Entities.VIEW, customViewCopy.id, customViewCopy);

			if (createRes.successful) {
				dispatch(customViewCleared());
				dispatch(customFilterCleared());

				await RefreshCache(dispatch);
			} else {
				toast.error("Error. " + createRes.errorCode + ": " + createRes.errorMsg);
			}
		}
	}

	const panelTitle = customView != null && customView.id == undefined ? "New Custom View" :
		customView != null && customView.id != null ? "Editing View"
			: "Filters";

	const visibilityOptions: DropdownOption<ViewVisibility>[] = [
		{ label: "Selected teams", value: ViewVisibility.TEAMS },
		{ label: "Selected agents", value: ViewVisibility.AGENTS },
	];

	if (agent?.id == customView?.creatorAgentId) {
		visibilityOptions.push({ label: "Just me", value: ViewVisibility.PRIVATE });
	}

	// Don't let non-admins add 'everyone' views.
	if (agent?.isAdmin) {
		visibilityOptions.push({ label: "Everyone", value: ViewVisibility.GLOBAL });
	} else {
		visibilityOptions.push({
			label: "Everyone (only available to admins)",
			value: ViewVisibility.GLOBAL,
			isDisabled: true
		});
	}

	return (
		<motion.div
			initial={{ width: 0 }}
			animate={{ width: 320 }}
			exit={{ width: 0 }}
			transition={{ duration: 0.2 }}
		>
			<div className="bg-white border-l border-gray-300 w-[320px] flex flex-col h-full">
				<div className="flex justify-between items-center px-7 py-5 w-full">
					<div className="text-xl font-medium text-gray-600 line-clamp-1 overflow-clip">{panelTitle}</div>
					<div className="flex items-center shrink-0 gap-1">
						<Tooltip title="Reset Filters" placement="bottom">
							<IconButton variant="plain" onClick={resetFilters}>
								<FaArrowRotateRight className="text-lg" onClick={resetFilters} />
							</IconButton>
						</Tooltip>

						<Tooltip title="Clear Filters" placement="bottom">
							<IconButton variant="plain" onClick={clearFilters}>
								<BiEraser className="text-2xl" onClick={clearFilters} />
							</IconButton>
						</Tooltip>

						<Tooltip title="Close Filters" placement="bottom">
							<IconButton variant="plain" onClick={toggleFilters}>
								<IoCloseOutline className="text-3xl" />
							</IconButton>
						</Tooltip>
					</div>
				</div>

				<div className="grow overflow-auto px-7 py-5 ">
					<TextInput
						label="Subject"
						dataname="description"
						value={getStringFilterValue("description")}
						onChangeDebounced={setStringFilterValue}
						clearable
					/>

					<MultiEntitySelect
						dataname="statusId"
						label="Statuses"
						cacheSlice={CacheSlices.Statuses}
						value={getMultiNumberFilterValue("statusId")}
						handleSubmit={setMultiNumberFilterValue}
					/>
					<MultiEntitySelect
						dataname="teamId"
						label="Teams"
						cacheSlice={CacheSlices.Teams}
						value={getMultiNumberFilterValue("teamId")}
						handleSubmit={setMultiNumberFilterValue}
					/>
					<MultiEntitySelect
						dataname="agentId"
						label="Agents"
						extraOptions={[{ value: -1, label: "Unassigned" }]}
						cacheSlice={CacheSlices.Agents}
						value={getMultiNumberFilterValue("agentId")}
						handleSubmit={setMultiNumberFilterValue}
					/>
					<MultiEntitySelect
						dataname="priorityId"
						label="Priorities"
						cacheSlice={CacheSlices.Priorities}
						value={getMultiNumberFilterValue("priorityId")}
						handleSubmit={setMultiNumberFilterValue}
					/>
					<MultiEntitySelect
						dataname="slaId"
						label="SLAs"
						cacheSlice={CacheSlices.Slas}
						value={getMultiNumberFilterValue("slaId")}
						handleSubmit={setMultiNumberFilterValue}
					/>
					<MultiEntitySelect
						dataname="companyId"
						label="Companies"
						extraOptions={[{ value: -1, label: "No company" }]}
						cacheSlice={CacheSlices.Companies}
						value={getMultiNumberFilterValue("companyId")}
						handleSubmit={setMultiNumberFilterValue}
					/>
					<MultiEntitySelect
						dataname="usersId"
						label="Users"
						extraOptions={[{ value: -1, label: "No user" }]}
						cacheSlice={CacheSlices.Users}
						value={getMultiNumberFilterValue("usersId")}
						handleSubmit={setMultiNumberFilterValue}
					/>

					{!customView &&
						<Button label="Add custom view" btnClass="btn-blue" onClick={() => dispatch(customViewCreated())} />}
				</div>

				{customView &&
					<div className="px-7 py-5 border-t">

						<div className="flex gap-2">
							<EmojiKeyboard label="Icon" updateValue={updateNewIcon} value={customView.icon} />
							<TextInput
								mandatory
								dataname="name"
								label="Name"
								value={customView.name}
								onChange={updateNewCustomViewName}
							/>
						</div>

						<div className="pb-5">
							<Dropdown<ViewVisibility>
								dataname="visibility"
								label="Visibility"
								value={customView.visibility ?? ViewVisibility.PRIVATE}
								options={visibilityOptions}
								onChange={updateVisibility}
							/>

							{customView.visibility == ViewVisibility.AGENTS &&
								<MultiEntitySelect
									dataname="AgentId"
									label="Agents"
									mandatory
									cacheSlice={CacheSlices.Agents}
									value={customView.visibilityAgentsIds}
									handleSubmit={updateAgentVisibility}
								/>}

							{customView.visibility == ViewVisibility.TEAMS &&
								<MultiEntitySelect
									dataname="TeamId"
									label="Teams"
									mandatory
									cacheSlice={CacheSlices.Teams}
									value={customView.visibilityTeamsIds}
									handleSubmit={updateTeamVisibility}
								/>}
						</div>

						<div className="flex gap-4">
							<Button className="grow justify-center" label="Cancel" btnClass="btn-grey" onClick={() => dispatch(customViewCleared())} />

							{customView.id == undefined ?
								<Button className="grow justify-center" label="Create view" btnClass="btn-blue" onClick={postView} />
								:
								<Button className="grow justify-center" label="Update view" btnClass="btn-blue" onClick={updateView} />}
						</div>
					</div>
				}
			</div>
		</motion.div>
	);
}
